'use client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TestResult, TestResultStatistics } from '@/types';
import { getCityGoalStatus, getNearestCityGoal, getPoints } from '@/utils/client/test-feedback';

const chartMaxPr = 100;

type CohortRunMarker = {
  id: string;
  run: number;
  pr: number;
  position: number;
};

function getCohortRunMarkers(cohortRuns: TestResult[]): CohortRunMarker[] {
  return [...cohortRuns]
    .sort((a, b) => (a.testRun ?? 0) - (b.testRun ?? 0))
    .map((result, index) => {
      const pr = result.statistics.percentageRanking;
      if (pr == null || pr <= 0) return null;

      return {
        id: result._id,
        run: result.testRun ?? index + 1,
        pr,
        position: Math.min(100, Math.max(0, (pr / chartMaxPr) * 100)),
      };
    })
    .filter((marker): marker is CohortRunMarker => marker != null);
}

export default function TestGoalDistance({
  statistics,
  cohortRuns,
  currentResultId,
}: {
  statistics: TestResultStatistics;
  cohortRuns: TestResult[];
  currentResultId: string;
}) {
  const { t } = useTranslation();
  const [hoveredPr, setHoveredPr] = useState<number | null>(null);
  const { percentageRanking } = statistics;
  const nearestGoal = getNearestCityGoal(percentageRanking);
  const cityStatuses = getCityGoalStatus(percentageRanking);
  const runMarkers = getCohortRunMarkers(cohortRuns);

  if (!nearestGoal) return null;

  if (percentageRanking === -1) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 0.375 }}>
          {t('test.insight.goal.title')}
        </Typography>
        <Alert severity="info" variant="outlined">
          {t('test.insight.goal.insufficientData')}
        </Alert>
      </Box>
    );
  }

  const currentPr = percentageRanking != null && percentageRanking > 0 ? percentageRanking : 0;
  const activePr = hoveredPr ?? currentPr;
  const chartCityStatuses = getCityGoalStatus(activePr);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 0.375 }}>
        {t('test.insight.goal.title')}
      </Typography>
      {statistics.correct === 0 ? (
        <Alert severity="info" variant="outlined">
          {t('test.insight.goal.zeroCorrect', { city: nearestGoal.name, target: nearestGoal.pr })}
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('test.insight.goal.yourPr', { pr: currentPr.toFixed(1) })}
          </Typography>
          {percentageRanking != null && percentageRanking > -1 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mb: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">{t('test.insight.goal.explanationTitle')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" component="div">
                  <Trans
                    i18nKey="test.insight.goal.explanation"
                    values={{
                      points: getPoints(statistics).toFixed(2),
                      pr: currentPr.toFixed(1),
                    }}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box
              sx={{
                minWidth: 88,
                display: { xs: 'none', sm: 'flex' },
                flexDirection: 'column',
                gap: 3,
                pt: 0.75,
              }}>
              {cityStatuses.map((city) => (
                <Typography
                  key={city.name}
                  variant="body2"
                  sx={{ height: 28, display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  {city.name}
                </Typography>
              ))}
            </Box>
            <Box sx={{ flex: 1, position: 'relative', minWidth: 0, pb: 4 }}>
              {runMarkers.map((marker) => {
                const isCurrent = marker.id === currentResultId;

                return (
                  <Box
                    key={marker.id}
                    onMouseEnter={() => setHoveredPr(marker.pr)}
                    onMouseLeave={() => setHoveredPr(null)}
                    sx={{
                      position: 'absolute',
                      left: `${marker.position}%`,
                      top: 0,
                      bottom: 0,
                      width: 20,
                      display: 'flex',
                      justifyContent: 'center',
                      transform: 'translateX(-50%)',
                      zIndex: isCurrent ? 3 : 2,
                      cursor: isCurrent ? 'default' : 'pointer',
                      borderRadius: 2,
                      '&:hover': {
                        zIndex: 10,
                      },
                      '&:hover .goal-run-line': {
                        opacity: 1,
                      },
                      '&:hover .goal-run-flag': {
                        opacity: 1,
                      },
                    }}>
                    <Box
                      className="goal-run-line"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: -8,
                        width: 3,
                        bgcolor: 'primary.main',
                        opacity: isCurrent ? 1 : 0.4,
                        transition: 'opacity 0.15s ease',
                        borderRadius: 2,
                      }}
                    />
                    <Box
                      className="goal-run-flag"
                      sx={{
                        position: 'absolute',
                        bottom: -24,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: 'common.black',
                        bgcolor: 'primary.main',
                        whiteSpace: 'nowrap',
                        opacity: isCurrent ? 1 : 0,
                        transition: 'opacity 0.15s ease',
                        pointerEvents: 'none',
                      }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                        {t('test.insight.goal.runPrFlag', {
                          run: marker.run,
                          pr: marker.pr.toFixed(1),
                        })}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 0.75 }}>
                {chartCityStatuses.map((city) => {
                  const barWidth = (city.pr / chartMaxPr) * 100;
                  const barColor = city.met ? '#28a745' : '#dc3545';

                  return (
                    <Box
                      key={city.name}
                      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box
                        sx={{
                          display: { xs: 'flex', sm: 'none' },
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {city.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            city.met
                              ? t('test.insight.goal.metBadge')
                              : t('test.insight.goal.unmetBadge')
                          }
                          color={city.met ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ position: 'relative', height: 28 }}>
                        <Box
                          aria-hidden
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 6,
                            height: 16,
                            width: '100%',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 6,
                            height: 16,
                            width: `${barWidth}%`,
                            maxWidth: '100%',
                            bgcolor: barColor,
                            borderRadius: 1,
                            opacity: 0.85,
                            transition: 'background-color 0.15s ease',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            left: `min(calc(${barWidth}% + 6px), calc(100% - 48px))`,
                            top: 6,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                          }}>
                          {city.pr} PR
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box
              sx={{
                width: 152,
                display: { xs: 'none', sm: 'flex' },
                flexDirection: 'column',
                gap: 3,
                pt: 0.75,
                flexShrink: 0,
              }}>
              {chartCityStatuses.map((city) => (
                <Box key={city.name} sx={{ height: 28, display: 'flex', alignItems: 'center' }}>
                  <Chip
                    size="small"
                    label={
                      city.met ? t('test.insight.goal.metBadge') : t('test.insight.goal.unmetBadge')
                    }
                    color={city.met ? 'success' : 'error'}
                    variant="outlined"
                    sx={{ width: '100%' }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
